import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from "react";
import { RCCButton, RCCGrid, RCCHookToast, RCCInput, RCCRefModalProps, RCCServicesApi, RCCTextArea, RCCTooltip } from "react-common-components";
import { useForm } from "react-hook-form";
import { useLoaderBackdrop } from "src/context/LoaderBackdrop/context";
import { useTranslation } from "src/context/Translations/context";
import useEffectSkipInitialRender from "src/hooks/useEffectSkipInitialRender";
import { getValidationErrors, standardErrorMessages } from "src/utils/errors";
import Modal from "../Modal";
import Select from "../Select";
import Title from "../Title";
import ListFields from "./components/ListFields";
import { IPeriodType } from "./components/ListFields/interface";
import { IFilterMeta, IModalFormulaAssistant, IRefModalFormulaAssistant } from "./interface";
import validateFormulaModal from "./schemas";

const ModalFormulaAssistant: React.ForwardRefRenderFunction<IRefModalFormulaAssistant, IModalFormulaAssistant> = ({
	methodRequestValuesFormula = "Indicators/ListFormulaFields",
	showFieldFilter = true,
	showOnlyDescription = false
}, ref) => {

	const { translate } = useTranslation();
	const { setLoading } = useLoaderBackdrop();
	const {addToast} = RCCHookToast();

	const [data, setData] = React.useState<any>();
	const [selectedIndicator, setSelectedIndicator] = React.useState<number | undefined>(undefined);

	const onCallbackFunction = useRef<Function | undefined>(undefined);

	const modalFormulaAssistant = useRef<RCCRefModalProps>(null);

	const [dictionaryFields, setDictionaryFields] = useState<{ id: number, code: string, description: string }[]>([]) ;

	const [filters, setFilters] = useState<{filter?: string, meta: IFilterMeta}>({ 
		filter: "" ,
		meta: IFilterMeta.ALL
	});

	const [ valueFormula, setValueFormula ] = useState<string>("");
	const [ formulaText, setFormulaText] = useState<string>("");

	const { 
		register,
		setValue,
		reset,
		setError,
		formState: { errors },
		clearErrors,
		handleSubmit
	} = useForm();

	const { 
		handleSubmit: handleSubmitFilters, 
		register: registerFilters, 
		reset: resetFilters,
		control: controlFilters
	} = useForm();

	const loadData = async (init?: boolean, initialId?: number, idField?: number) => {
		setLoading(true);
		try{	
			const paramters = initialId ? { idIndicator: initialId ?? "", idField: idField ?? "" } : undefined;

			const response  = await RCCServicesApi.getRequest({
				method: methodRequestValuesFormula,
				urlParameters: init ? {
					idIndicator: paramters?.idIndicator ?? "",
					idField: paramters?.idField ?? ""
				} : {
					...filters,
					idIndicator: selectedIndicator ?? ""
				}
			});

			const sortedArray = [
				...response.data.filter((dataY: any) => dataY.hasLink),
				...response.data.filter((dataY: any) => !dataY.hasLink)
			];

			setData(sortedArray);
			setDictionaryFields((prevState) => sortedArray.map((item: any) => ({
				id: item.iqb001Id,
				code: item.iqb001Codigo,
				description: item.iqb001Label
			})).concat(prevState));
			setLoading(false);
			modalFormulaAssistant.current?.openModal();
		}catch(err){
			standardErrorMessages(err, translate("Erro ao buscar os dados"), addToast);
			setLoading(false);
		}
	};

	const openModal = (idIndicator: number, defaultFormula?: string, callbackFormula?: Function, idField?: number) => {
		setSelectedIndicator(idIndicator || undefined);
		setData(null);
		resetFilters();
		reset();
		setValueFormula(defaultFormula ?? "");
		setValue("formula", defaultFormula ?? "");
		onCallbackFunction.current = callbackFormula;
		loadData(true, idIndicator, idField);
	};

	useImperativeHandle(ref, () => ({
		openModal
	}));


	const submitFilters = (dataX: any) => {
		setFilters({
			filter: dataX.filter ?? "",
			meta: dataX.meta ? objToString(dataX.meta) : IFilterMeta.ALL
		});
	};

	useEffectSkipInitialRender(() => {
		loadData();
	}, [filters]);

	const objToString = (obj: any) => {
		if(typeof obj === "object")
			return obj.value;
		if(typeof obj === "number")
			return obj.toString();
		if(typeof obj !== "string")
			return "";
		return obj;
	};

	const clickField = (id: number, code: string, description: string, periodType: IPeriodType, periods: number) => {
		dictionaryFields.push({ id,code, description });

		const getPeriods = () => {
			const signal = periodType === IPeriodType.BACK ? " - " : " + ";
			return `${signal}${periods}`;
		};
		const acrescentPeriod = periodType === IPeriodType.CURRENT || periods === 0  ? "" : getPeriods();
		const valueToIncrement = `[${code}${acrescentPeriod}]`;
		setValueFormula((prevState) => prevState ? `${prevState}${valueToIncrement}`: valueToIncrement);
	};

	const updateTextFormula = () => {
		let value = valueFormula;

		// eslint-disable-next-line no-useless-escape
		const regex = /(?:\[)[^\]\[\r\n]*(?:\])/gm;
		// The regex that was used is /(?<=\[)[^\]\[\r\n]*(?=\])/gm
		// It was removed because it contains Lookbehind in JS regular expressions
		// And the new IE or Safari, call the name you prefer, don't support that feature
		// For details check the links below:
		// https://caniuse.com/js-regexp-lookbehind
		let controlRegex;

		const getTextFormula = (formula: string) => {

			if(formula.trim() === ""){
				return "";
			}
			const havePlus = /[+]/g.test(formula);
			const haveMinus = /[-]/g.test(formula);

			if(havePlus || haveMinus){
				const fieldAndPeriods = formula.split(/[+-]/);
				const titleField = dictionaryFields.find((field) => field.code === fieldAndPeriods[0].trim())?.description ?? translate("Desconhecido");
				return `${titleField} (${translate("período")}${havePlus ? "+" : "-" }${fieldAndPeriods[1].trim()})`;
			}

			const titleField = dictionaryFields.find((field) => field.code === formula)?.description ?? translate("Desconhecido");
			return `[${titleField}]`;
		};

		// eslint-disable-next-line no-cond-assign
		while ((controlRegex = regex.exec(value)) !== null) {
			// This is necessary to avoid infinite loops with zero-width matches
			if (controlRegex.index === regex.lastIndex) {
				regex.lastIndex += 1;
			}
	
			// The result can be accessed through the `m`-variable.
			// eslint-disable-next-line no-loop-func
			controlRegex.forEach((match) => {
				const textFormula = match.replace("[", "").replace("]", "");
				value = value.replace(match, getTextFormula(textFormula));
			});
		}
		setFormulaText(value);
	};	

	const FieldsList = useMemo(() => (
		<ListFields 
			data={data}
			clickField={clickField}
			selectedIndicator={selectedIndicator ?? 0}
			showOnlyDescription={showOnlyDescription}
		/>
	), [data]);

	useEffect(() => {
		updateTextFormula();
	}, [valueFormula, dictionaryFields]);

	const onSubmitModal = async () => {
		clearErrors();
		try{
			modalFormulaAssistant.current?.setLoadingButton();
			await validateFormulaModal(translate).validate({
				formula: valueFormula
			}, {
				abortEarly: false
			});

			if(onCallbackFunction.current)
				onCallbackFunction.current(valueFormula, formulaText);
			modalFormulaAssistant.current?.resetLoadingButton();
			modalFormulaAssistant.current?.closeModal();

		}catch(err: any){
			modalFormulaAssistant.current?.resetLoadingButton();
			 getValidationErrors(err, setError);
		}

	};

	return (
		<>
			<Modal
				ref={modalFormulaAssistant}
				header={{
					title:  translate("Assistente de Fórmulas"),
					icon: {
						name: "xSquare"
					}
				}}
				withoutForm
				onSubmit={handleSubmit(onSubmitModal)}
			>
				<RCCGrid
					container
					style={{ height: "100%" }}
				>
					<RCCGrid
						container
						xs={12}
						sm={6}
						item	
						direction="column"
						styleInline="font-size: 0.75rem; padding: 0.5rem; textarea{ min-height: 150px;}"
					>
						<Title
							size="sm"
							color="black"
						>
							{translate("Legenda")}:
						</Title>

						<p><b>{translate("Operadores")}:</b> {`${translate("Adição")} (+), ${translate("Subtração")} (-), ${translate("Multiplicação")} (*), ${translate("Divisão")} (/) e ${translate("Parenteses")}`} </p>
						<p><b>{translate("Números")}:</b> {`${translate("Inteiros")} (Ex.: 123) e ${translate("Reais")} (Ex.: 0.789)`}</p>
						<p><b>{translate("Campos")}:</b> [{translate("CodigoCampo")}] (Ex.: [456]) </p>

						<RCCGrid
							container
							item
							styleInline="textarea{ min-height: 150px !important;}"
						>
							<RCCTextArea 
								register={register}
								name="formula"
								label={translate("Fórmula")}
								onChange={(e) => { setValueFormula(e.target.value); }}
								value={valueFormula}
								error={errors.formula}
							/>	
						</RCCGrid>
						<RCCGrid
							container
							item
							direction="column"
							styleInline="textarea{ min-height: 150px !important;}"
						>
							<RCCTextArea 
								register={register}
								name="formulaText"
								label={translate("Fórmula em texto")}
								spellCheck="false"
								value={formulaText}
								disabled
							/>
						</RCCGrid>
					</RCCGrid>
					<RCCGrid
						item
						container
						xs={12}
						sm={6}
						direction="column"
						styleInline="padding: 0.5rem;"
						style={{ height: "100%", display: "flex", flexWrap: "nowrap" }}
					>
						<RCCGrid
							container
						>
							<RCCGrid
								container
								spacing={1}
								alignContent="center"
								justifyContent="center"
								alignItems="baseline"
							>	
								<RCCGrid container item 
									xs={showFieldFilter ? 7 : 10} 
								>
									<RCCInput 
										register={registerFilters}
										name="filter" 
										label={translate("Filtro")}
									/>
								</RCCGrid>
								{showFieldFilter && (
									<RCCGrid container item 
										xs={3} 
									>
										<Select
											register={registerFilters}
											control={controlFilters}
											name="meta" 
											label={translate("Campos")}
											defaultValue={{
												label: translate("Todos"),
												value: IFilterMeta.ALL
											}}
											options={[{
												label: translate("Apenas metas"),
												value: IFilterMeta.ONLY_METAS
											},{
												label: translate("Apenas campos"),
												value: IFilterMeta.ONLY_FIELDS
											},{
												label: translate("Todos"),
												value: IFilterMeta.ALL
											}]}
											submit="value and label"
										/>
									</RCCGrid>
								)}
								<RCCGrid 
									container 
									item
									justifyContent="center"
									xs={2} 
								>
									<RCCTooltip
										text={translate("Aplicar filtro")}
										color="blue"
									>
										<RCCButton
											icon={{name: "search", color: "#FFF", size: 16}}
											color="blue"
											layout="isRounded"
											size="xs"
											onClick={handleSubmitFilters(submitFilters)}
										/>
									</RCCTooltip>
									<RCCTooltip
										text={translate("Limpar filtros")}
										color="yellow"
									>
										<RCCButton
											icon={{name: "broom", color: "#FFF", size: 16}}
											color="yellow"
											layout="isRounded"
											size="xs"
											onClick={() => resetFilters({ 
												filter: "",
												meta: { value: IFilterMeta.ALL, label: translate("Todos") } 
											})}
										/>
									</RCCTooltip>
								</RCCGrid>	
							</RCCGrid>			
						</RCCGrid>	

						<RCCGrid
							container
							style={{ height: "100%", overflowX: "hidden", overflowY: "scroll" }}
						>
							{FieldsList}
						</RCCGrid>		
						
					</RCCGrid>
				</RCCGrid>

			</Modal>
		</>
	);

};

export default React.forwardRef(ModalFormulaAssistant);
